import { frameId, type ManifestObjectAppearance, type TileRef } from "daedalus-core";
import { NO_HANDLE, TICK_MS, ThingEventCode, type ThingEventRecord, type ThingFrame } from "odyssey-shared";

/**
 * The look of each shrine on this viewer's page (ADR-096 decisions 16, 18 to 20, 24, 25 and 29). The wire's state
 * byte of a shrine never changes, so the page chooses what a shrine rests on from its own character's set, which the
 * server tells it in `shrines`: `available`, the loop, for a shrine whose handle is in the set, and `dormant`, the plain
 * stone, for every other, from first sight and before any set has arrived. The pop is the strip of the `attune`
 * transition, played once over the resting look and never a state:
 *
 * - an arrival's `thingEvent` plays it for a shrine in view, and is ignored for a handle not in view and while that
 *   shrine's pop is still playing, so a strip is never restarted half way and never queued; a pulse within one tick
 *   of the strip's end is played, since the map server spaces two pops by the strip's own length and the second may
 *   arrive a few milliseconds early;
 * - the page's own discovery plays it once, for a shrine in view, as the look changes to the loop;
 * - a shrine that enters view rests at once: nothing is replayed for a page that gained sight mid-pop;
 * - under reduced motion the strip is not drawn and the look snaps; the pop still counts as playing for its length,
 *   so what sounds and what is ignored do not depend on the setting.
 *
 * A welcome forgets the set, every pop and every shrine in view. Frames are prepared once an appearance, so asking
 * what to draw allocates nothing.
 */

/**
 * The id of the state a discovered shrine rests on.
 */
const AVAILABLE = "available";

/**
 * The id of the state an undiscovered shrine rests on.
 */
const DORMANT = "dormant";

/**
 * A shrine appearance ready to draw: the two resting states' indices and frames, and the pop's frames with the time
 * each ends, in milliseconds from its start; `total` is 0 for a shrine with no pop strip chosen, which cuts.
 */
interface PreparedShrine {
  readonly available: number;
  readonly dormant: number;
  readonly availableFrame: ThingFrame;
  readonly dormantFrame: ThingFrame;
  readonly frames: readonly ThingFrame[];
  readonly ends: readonly number[];
  readonly total: number;
}

/**
 * A shrine in view: its appearance, when its pop started (negative for none), and the sync that last listed it.
 */
interface HeldShrine {
  appearance: number;
  popStart: number;
  listed: number;
}

/**
 * A tile reference with its frame id.
 *
 * @param ref The tile reference.
 * @returns The frame.
 */
function frameOf(ref: TileRef): ThingFrame {
  return { ref, id: frameId(ref.tileset, null, ref.tile) };
}

/**
 * Prepares an appearance as a shrine's.
 *
 * @param look The appearance.
 * @returns The prepared shrine, or null for an appearance without the two states a shrine rests on.
 */
function prepare(look: ManifestObjectAppearance): PreparedShrine | null {
  const available = look.states.findIndex((state) => state.id === AVAILABLE);
  const dormant = look.states.findIndex((state) => state.id === DORMANT);
  const availableState = look.states[available];
  const dormantState = look.states[dormant];
  if (availableState === undefined || dormantState === undefined) {
    return null;
  }
  const pop = look.transitions?.find((transition) => transition.from === dormant && transition.to === available);
  const ends: number[] = [];
  let total = 0;
  for (const duration of pop?.durations ?? []) {
    total += duration;
    ends.push(total);
  }
  return { available, dormant, availableFrame: frameOf(availableState.frame), dormantFrame: frameOf(dormantState.frame), frames: (pop?.frames ?? []).map(frameOf), ends, total };
}

/**
 * The shrines of one page: its character's discovered set, the shrines in view and their pops.
 *
 * @example
 * ```ts
 * const looks = new ShrineLooks(world.manifest.objectAppearances ?? []);
 * looks.receive([12], true, 12, now);
 * looks.frame(12, now, false);
 * ```
 */
export class ShrineLooks {
  private readonly prepared: readonly (PreparedShrine | null)[];
  private readonly discovered = new Set<number>();
  private readonly held = new Map<number, HeldShrine>();
  private listing = 0;

  /**
   * How many times the set has changed, which the things' drawing reads to know its placed frames are stale.
   */
  public version = 0;

  /**
   * How many pops have started, for the page's diagnostics.
   */
  public pops = 0;

  /**
   * @param appearances The world version's appearance table.
   */
  public constructor(appearances: readonly ManifestObjectAppearance[]) {
    this.prepared = appearances.map(prepare);
  }

  /**
   * Forgets the set, every pop and every shrine in view, as a welcome must: every shrine is drawn dormant until the
   * session's own `shrines` arrives.
   */
  public reset(): void {
    if (this.discovered.size > 0) {
      this.discovered.clear();
      this.version += 1;
    }
    this.held.clear();
  }

  /**
   * Takes a `shrines` message: the whole set replaces the one held. A discovery of a shrine the set did not already
   * hold is fresh, and plays that shrine's pop once when it is in view and none is playing; a discovery of a shrine
   * that is no thing (`found` 65535, a marker) is fresh every time, since the server tells each once.
   *
   * @param handles The handles of the shrines the page's character has discovered.
   * @param discovered Whether a discovery caused the message.
   * @param found The handle of the shrine just discovered, 65535 for none or for one that is no thing.
   * @param nowMs The game loop's time in ms.
   * @returns Whether the message tells a discovery the page did not already hold.
   */
  public receive(handles: readonly number[], discovered: boolean, found: number, nowMs: number): boolean {
    const fresh = discovered && (found === NO_HANDLE || !this.discovered.has(found));
    let changed = handles.length !== this.discovered.size;
    for (let i = 0; i < handles.length && !changed; i += 1) {
      changed = !this.discovered.has(handles[i] as number);
    }
    if (changed) {
      this.discovered.clear();
      for (const handle of handles) {
        this.discovered.add(handle);
      }
      this.version += 1;
    }
    if (fresh && found !== NO_HANDLE) {
      this.pulse(found, nowMs);
    }
    return fresh;
  }

  /**
   * Whether the page's character has discovered a shrine.
   *
   * @param handle The shrine's handle.
   * @returns True when the set holds it.
   */
  public has(handle: number): boolean {
    return this.discovered.has(handle);
  }

  /**
   * The handles the set holds, ascending, for the page's diagnostics.
   *
   * @returns A new list.
   */
  public handles(): number[] {
    return [...this.discovered].sort((a, b) => a - b);
  }

  /**
   * Starts listing the shrines in view; each `hold` that follows keeps one, and `endView` drops the rest.
   */
  public beginView(): void {
    this.listing += 1;
  }

  /**
   * Keeps a shrine in view. One the page did not hold, or holds under another appearance, is first sight and rests.
   *
   * @param handle The shrine's handle.
   * @param appearance Its appearance index.
   * @returns The index of the state it rests on for this viewer, or -1 when the appearance is no shrine's.
   */
  public hold(handle: number, appearance: number): number {
    const prepared = this.prepared[appearance];
    if (prepared === undefined || prepared === null) {
      return -1;
    }
    const held = this.held.get(handle);
    if (held === undefined) {
      this.held.set(handle, { appearance, popStart: -1, listed: this.listing });
    } else {
      if (held.appearance !== appearance) {
        held.appearance = appearance;
        held.popStart = -1;
      }
      held.listed = this.listing;
    }
    return this.discovered.has(handle) ? prepared.available : prepared.dormant;
  }

  /**
   * Drops every shrine the listing since `beginView` did not keep, with its pop.
   */
  public endView(): void {
    for (const [handle, held] of this.held) {
      if (held.listed !== this.listing) {
        this.held.delete(handle);
      }
    }
  }

  /**
   * Starts a shrine's pop, as an arrival's event or the page's own discovery asks.
   *
   * @param handle The shrine's handle.
   * @param nowMs The game loop's time in ms.
   * @returns False when the shrine is not in view or its pop is still playing with more than a tick left, so nothing
   *   started and nothing sounds.
   */
  public pulse(handle: number, nowMs: number): boolean {
    const held = this.held.get(handle);
    const prepared = held === undefined ? undefined : this.prepared[held.appearance];
    if (held === undefined || prepared === undefined || prepared === null) {
      return false;
    }
    if (held.popStart >= 0 && nowMs - held.popStart < prepared.total - TICK_MS) {
      return false;
    }
    held.popStart = nowMs;
    this.pops += 1;
    return true;
  }

  /**
   * The frame a shrine shows now: its pop's frame while one plays and motion is not reduced, otherwise the look it
   * rests on for this viewer.
   *
   * @param handle The thing's handle.
   * @param nowMs The game loop's time in ms.
   * @param reduced Whether the page's motion is reduced.
   * @returns The frame, or null when the handle is no shrine in view.
   */
  public frame(handle: number, nowMs: number, reduced: boolean): ThingFrame | null {
    const held = this.held.get(handle);
    if (held === undefined) {
      return null;
    }
    const prepared = this.prepared[held.appearance] as PreparedShrine;
    if (held.popStart >= 0) {
      const elapsed = nowMs - held.popStart;
      if (elapsed >= prepared.total) {
        held.popStart = -1;
      } else if (!reduced) {
        for (let i = 0; i < prepared.ends.length; i += 1) {
          if (elapsed < (prepared.ends[i] as number)) {
            return prepared.frames[i] as ThingFrame;
          }
        }
      }
    }
    return this.discovered.has(handle) ? prepared.availableFrame : prepared.dormantFrame;
  }
}

/**
 * Plays a `thingEvent` frame's events (ADR-096 decision 22): each `pulse` asks its thing's pop, and one that started
 * is told, so its sound plays; an event code this page does not know is skipped, and so is a pulse the things ignore
 * (a handle not in view, a pop still playing).
 *
 * @param events The decoded frame.
 * @param pulse Asks a thing's pop; false when it is ignored.
 * @param started Told the handle of each thing whose pop started.
 */
export function playThingEvents(events: Readonly<ThingEventRecord>, pulse: (handle: number) => boolean, started: (handle: number) => void): void {
  for (let i = 0; i < events.count; i += 1) {
    const handle = events.handles[i] as number;
    if (events.events[i] === ThingEventCode.Pulse && pulse(handle)) {
      started(handle);
    }
  }
}
